import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Trophy, Users, Calendar, Clock, DollarSign, 
  ChevronRight, Medal, Target, Zap
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TournamentsPage = () => {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tournaments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(response.data);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const fetchLeaderboard = async (tournamentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tournaments/${tournamentId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const enterTournament = async (tournamentId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tournaments/${tournamentId}/enter`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Successfully entered tournament!');
      fetchTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter tournament');
    } finally {
      setLoading(false);
    }
  };

  const selectTournament = async (tournament) => {
    setSelectedTournament(tournament);
    await fetchLeaderboard(tournament.id);
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      active: "bg-green-500/10 text-green-500 border-green-500/30",
      completed: "bg-gray-500/10 text-gray-500 border-gray-500/30"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.upcoming}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-white tracking-tighter uppercase">
            TOURNAMENTS
          </h1>
          <p className="text-muted-foreground mt-1">Compete against other athletes for prizes</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Tournament List */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white">Available Tournaments</h2>
            
            {tournaments.length > 0 ? (
              tournaments.map((tournament) => (
                <Card 
                  key={tournament.id}
                  className={`bg-card border-border cursor-pointer transition-all hover:border-primary/50 ${
                    selectedTournament?.id === tournament.id ? 'border-primary' : ''
                  }`}
                  onClick={() => selectTournament(tournament)}
                  data-testid={`tournament-${tournament.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center">
                          <Trophy className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-white">{tournament.name}</h3>
                          {getStatusBadge(tournament.status)}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center mt-4">
                      <div>
                        <DollarSign className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
                        <p className="text-sm font-bold text-white">{tournament.prize_pool}</p>
                        <p className="text-xs text-muted-foreground">Prize Pool</p>
                      </div>
                      <div>
                        <Users className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                        <p className="text-sm font-bold text-white">
                          {tournament.participants}/{tournament.max_participants || '∞'}
                        </p>
                        <p className="text-xs text-muted-foreground">Athletes</p>
                      </div>
                      <div>
                        <Calendar className="w-4 h-4 mx-auto text-green-500 mb-1" />
                        <p className="text-sm font-bold text-white">{formatDate(tournament.start_date)}</p>
                        <p className="text-xs text-muted-foreground">Starts</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No tournaments available</p>
                  <p className="text-sm text-muted-foreground mt-1">Check back soon for upcoming competitions!</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Tournament Details */}
          <div className="space-y-4">
            {selectedTournament ? (
              <>
                <Card className="bg-card border-border" data-testid="tournament-details">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl">{selectedTournament.name}</CardTitle>
                      {getStatusBadge(selectedTournament.status)}
                    </div>
                    <CardDescription>
                      {formatDate(selectedTournament.start_date)} - {formatDate(selectedTournament.end_date)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-background rounded-sm text-center">
                        <DollarSign className="w-6 h-6 mx-auto text-yellow-500 mb-2" />
                        <p className="text-2xl font-black text-white">{selectedTournament.prize_pool}</p>
                        <p className="text-xs text-muted-foreground">Prize Pool</p>
                      </div>
                      <div className="p-4 bg-background rounded-sm text-center">
                        <Target className="w-6 h-6 mx-auto text-primary mb-2" />
                        <p className="text-2xl font-black text-white">${selectedTournament.entry_fee}</p>
                        <p className="text-xs text-muted-foreground">Entry Fee</p>
                      </div>
                    </div>

                    {selectedTournament.status === 'upcoming' && (
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90 font-bold uppercase"
                        onClick={() => enterTournament(selectedTournament.id)}
                        disabled={loading}
                        data-testid="enter-tournament-btn"
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        {loading ? 'Entering...' : 'Enter Tournament'}
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Leaderboard */}
                <Card className="bg-card border-border" data-testid="tournament-leaderboard">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Medal className="w-5 h-5 text-yellow-500" />
                      Leaderboard
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {leaderboard.length > 0 ? (
                      <div className="space-y-2">
                        {leaderboard.map((entry, index) => (
                          <div 
                            key={index}
                            className={`flex items-center justify-between p-3 rounded-sm ${
                              index < 3 ? 'bg-primary/10' : 'bg-background'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 flex items-center justify-center rounded-full ${
                                index === 0 ? 'bg-yellow-500 text-black' :
                                index === 1 ? 'bg-gray-400 text-black' :
                                index === 2 ? 'bg-amber-600 text-black' :
                                'bg-muted text-muted-foreground'
                              } font-bold text-sm`}>
                                {entry.rank}
                              </span>
                              <span className="font-medium text-white">{entry.name}</span>
                            </div>
                            <span className="font-bold text-primary">{entry.score} pts</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        No entries yet. Be the first to compete!
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-card border-border h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Select a tournament to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentsPage;
